// // MODIFIED: Added execution guards to prevent auto-submission
import { useState, useCallback, useEffect } from "react";
import overtimeService from "../services/overtimeService";
import { useGeneralMessage } from "../../common/APICommon";
import { compressImage } from "../../utils/fileUtils";
import { FEATURES } from "../../config/features";

export const useOvertimeRequest = (user) => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  const [formData, setFormData] = useState({
    overtimeType: "",
    dateRequested: "",
    requestDateTimeFrom: "",
    requestDateTimeTo: "",
    hours: "",
    reason: "",
    hasOvertimeAllowance: false,
    isAboveTwoHoursOvertime: false,
    isOTStraightWithoutBreak: false,
    content: null,
    fileName: "",
    fileType: ""
  });

  const fetchHistory = useCallback(async () => {
    if (!user?.employeePersonalInformationId) return;
    try {
      setLoading(true);
      const res = await overtimeService.getByEmployee(user.employeePersonalInformationId);
      setRequests(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    if (FEATURES.ALLOW_OT_REQUEST_DATETIME && formData.requestDateTimeFrom && formData.requestDateTimeTo) {
      const start = new Date(formData.requestDateTimeFrom);
      const end = new Date(formData.requestDateTimeTo);
      const diff = (end - start) / (1000 * 60 * 60);
      setFormData(prev => ({ ...prev, hours: diff > 0 ? diff.toFixed(2) : "0" }));
    }
  }, [formData.requestDateTimeFrom, formData.requestDateTimeTo]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (file) => {
    if (!file) return;
    const MAX_SIZE_MB = 5;
    if (file.size > (MAX_SIZE_MB * 1024 * 1024)) {
      showGeneralMessage(`File is too large. Max size allowed is ${MAX_SIZE_MB}MB.`, false);
      return;
    }

    try {
      const reader = new FileReader();
      if (file.type.startsWith("image/")) {
        const compressed = await compressImage(file);
        reader.readAsDataURL(compressed);
      } else {
        reader.readAsDataURL(file);
      }

      reader.onload = () => {
        const base64Part = reader.result.split(",")[1];
        setFormData(prev => ({
          ...prev,
          content: base64Part,
          fileName: file.name,
          fileType: file.type
        }));
      };
    } catch (err) {
      showGeneralMessage("Failed to process file.", false);
    }
  };

  // // MODIFIED: Logic now strictly relies on being called by the Modal
  const submitRequest = async () => {
    if (loading) return; // // ADDED: Circuit breaker to prevent double-calls

    setLoading(true);
    try {
      const payload = {
        ...formData,
        date: formData.dateRequested,
        employeePersonalInformationId: user.employeePersonalInformationId,
        requestTypeId: 3,
        status: "Sent",
        hours: parseFloat(formData.hours)
      };

      await overtimeService.create(payload);

      showGeneralMessage("Overtime request submitted successfully!", true);
      await fetchHistory();

      // Reset Form State
      setFormData({
        overtimeType: "", dateRequested: "", hours: "", reason: "",
        requestDateTimeFrom: "", requestDateTimeTo: "",
        hasOvertimeAllowance: false, isAboveTwoHoursOvertime: false,
        isOTStraightWithoutBreak: false, content: null, fileName: "", fileType: ""
      });
      return true;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to submit request";
      showGeneralMessage(msg, false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading, requests, formData, handleInputChange,
    handleFileChange, submitRequest, showMessage, message, isSuccess
  };
};